import deviceApi from '@/api/device';
import posApi from '@/api/pos';
import DeviceKassa from '@/models/deviceKassa';
import Pos from '@/models/pos';
import { RootState } from '@/store';
import { ActionContext, GetterTree, MutationTree } from 'vuex';

interface DevicesState {
  poses: DeviceKassa[];
  pos: DeviceKassa | null;
  selectedPosId: string | null;
}

const namespaced: boolean = true;

const getDefaultState = (): DevicesState => ({
  poses: [],
  pos: null,
  selectedPosId: null
});

const state: DevicesState = getDefaultState();

const getters: GetterTree<DevicesState, RootState> = {
  hasMultiplePoses(state): boolean {
    return state.poses.length > 1;
  },
};

const actions = {
  async fetchPos({ commit }: ActionContext<DevicesState, RootState>) {
    const poses: DeviceKassa[] = await deviceApi.fetchPos();

    commit('setPoses', poses);
    commit('selectDefaultPos');
  },
  async selectPos({ commit }: ActionContext<DevicesState, RootState>, posId: string) {
    commit('selectPos', posId);

    return posApi.getPosState(posId)
      .then((posState: any) => commit('pos/setPosState', new Pos(posState), { root: true }))
      .catch(() => commit('pos/setPosState', new Pos(), { root: true }));
  }
};

const setSelectedPos = (state: DevicesState, pos: DeviceKassa | null) => {
  state.pos = pos;
  state.selectedPosId = pos !== null ? pos.id : null;

  if (pos !== null) {
    window.localStorage.setItem('posId', pos.id);
  } else {
    window.localStorage.removeItem('posId');
  }
};

const mutations: MutationTree<DevicesState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setPoses(state, poses: DeviceKassa[]) {
    state.poses = poses;
  },
  setPos(state, pos: DeviceKassa) {
    const index = state.poses.findIndex(({ id }: DeviceKassa) => id === pos.id);

    if (index === -1) {
      state.poses.push(pos);
    } else {
      state.poses.splice(index, 1, pos);
    }

    if (state.pos === null) {
      setSelectedPos(state, pos);
    }
  },
  selectDefaultPos(state) {
    const storedPosId = window.localStorage.getItem('posId');
    const pos = state.poses.find(({ id }: DeviceKassa) => id === storedPosId) || state.poses[0] || null;

    setSelectedPos(state, pos);
  },
  selectPos(state, posId: string) {
    const pos = state.poses.find(({ id }: DeviceKassa) => id === posId);

    if (pos !== undefined) {
      setSelectedPos(state, pos);
    }
  },
  removeDeviceKassa(state, posId: string) {
    const index = state.poses.findIndex(({ id }: DeviceKassa) => id === posId);

    if (index !== -1) {
      state.poses.splice(index, 1);
    }

    if (state.pos !== null && state.pos.id === posId) {
      setSelectedPos(state, state.poses[0] || null);
    }
  },
  editDeviceKassa(state, { id, ...editedFields }: any) {
    const pos = state.poses.find((pos: DeviceKassa) => pos.id === id);

    if (pos !== undefined) {
      pos.mergeWithNewFields(editedFields);
    }

    if (state.pos !== null && (id === undefined || state.pos.id === id)) {
      state.pos.mergeWithNewFields(editedFields);
    }
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
